import type { Ga4Property } from '@/api/types'
import { Badge, formatRelativeTime } from '@/design'

import { connectionLabel, freshnessLabel, isLive } from './status'

function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

interface Props {
  property: Ga4Property
  websiteName: string
  now: Date
  busy: boolean
  onRunNow: () => void
  onDisconnect: () => void
}

/** One property: its health, and Run import now (until its cooldown ends) and Disconnect. */
export function PropertyRow({ property, websiteName, now, busy, onRunNow, onDisconnect }: Props) {
  const status = connectionLabel(property)
  const freshness = freshnessLabel(property)
  const live = isLive(property)
  const cooling = property.nextManualImportAt !== null && new Date(property.nextManualImportAt) > now
  return (
    <tr>
      <th scope="row">
        {property.displayName}
        <small className="ci-integration__id">{property.propertyId}</small>
      </th>
      <td>{websiteName}</td>
      <td>
        <Badge tone={status.tone}>{status.label}</Badge>
        {property.status === 'error' && property.lastError ? <small className="ci-integration__id">{property.lastError}</small> : null}
      </td>
      <td>
        {property.lastImportAt ? formatRelativeTime(property.lastImportAt, now) : 'Not yet'}
        {property.lastImportStatus === 'failed' ? <small className="ci-integration__id">Last run failed</small> : null}
      </td>
      <td>{freshness ? <Badge tone={freshness.tone}>{freshness.label}</Badge> : '—'}</td>
      <td>
        {live ? (
          <span className="ci-integration__actions">
            <button
              type="button"
              className="ci-button"
              aria-label={`Run import now for ${property.displayName}`}
              disabled={busy || cooling}
              onClick={onRunNow}
            >
              Run import now
            </button>
            <button
              type="button"
              className="ci-button"
              data-variant="ghost"
              aria-label={`Disconnect ${property.displayName}`}
              disabled={busy}
              onClick={onDisconnect}
            >
              Disconnect
            </button>
            {cooling && property.nextManualImportAt ? (
              <small className="ci-integration__id">Available again at {timeOfDay(property.nextManualImportAt)}</small>
            ) : null}
          </span>
        ) : null}
      </td>
    </tr>
  )
}
